import React from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  LineChart,
  Line,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';
import { AmpelBadge, SignalListe, Abschnitt, FazitBox, LeerHinweis, farben } from 'core/components/ui';
import { formatKurs, formatZahl } from 'core/utils/format';

/** ISO-Datum → kompakter Achsen-Tick „MM/YY". */
const tickDatum = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('de-DE', { month: '2-digit', year: '2-digit' });
};

const achseStil = { fontSize: 11, fontFamily: 'JetBrains Mono, monospace', fill: farben.inkMuted };

/** Kleine, farbige Legenden-Markierung. */
function LegendePunkt({ farbe, label, dash }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-ink-muted">
      <span
        className="inline-block h-0.5 w-4"
        style={{ backgroundColor: farbe, borderTop: dash ? `2px dashed ${farbe}` : undefined, height: dash ? 0 : 2 }}
      />
      {label}
    </span>
  );
}

/** Tooltip für den Kurschart. */
function KursTooltip({ active, payload, label, currency }) {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0]?.payload || {};
  return (
    <div className="rounded-lg border border-line bg-paper-soft px-3 py-2 text-xs shadow-sm">
      <div className="mb-1 font-mono text-ink-muted">{new Date(label).toLocaleDateString('de-DE')}</div>
      <div className="font-mono text-ink">Schluss: {formatKurs(p.schluss, currency)}</div>
      {p.sma50 != null && <div className="font-mono text-ink-muted">SMA 50: {formatKurs(p.sma50, currency)}</div>}
      {p.sma200 != null && <div className="font-mono text-ink-muted">SMA 200: {formatKurs(p.sma200, currency)}</div>}
    </div>
  );
}

export default function ChartAnalyse({ chart, currency }) {
  if (!chart || !chart.kursReihe || chart.kursReihe.length === 0) {
    return <LeerHinweis>Für eine technische Analyse liegt zu wenig Kurshistorie vor.</LeerHinweis>;
  }

  return (
    <div>
      {/* Tendenz + Ampel */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line bg-paper-soft p-4">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-wider text-ink-muted">Charttechnische Tendenz</div>
          <div className="mt-0.5 font-serif text-2xl font-semibold text-ink">{chart.tendenz}</div>
        </div>
        <AmpelBadge ampel={chart.ampel} size="lg" />
      </div>

      {/* Kursverlauf mit gleitenden Durchschnitten + Bollinger */}
      <Abschnitt
        titel="Kursverlauf (≈ 1 Jahr)"
        aktion={
          <div className="hidden gap-3 sm:flex">
            <LegendePunkt farbe={farben.ink} label="Kurs" />
            <LegendePunkt farbe={farben.brand} label="SMA 50" />
            <LegendePunkt farbe={farben.inkMuted} label="SMA 200" />
            <LegendePunkt farbe={farben.line} label="Bollinger" dash />
          </div>
        }
      >
        <div className="rounded-xl border border-line bg-paper-soft p-2">
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={chart.kursReihe} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
              <CartesianGrid stroke={farben.line} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="datum" tickFormatter={tickDatum} tick={achseStil} minTickGap={44} stroke={farben.line} />
              <YAxis
                domain={['auto', 'auto']}
                tick={achseStil}
                width={52}
                stroke={farben.line}
                tickFormatter={(v) => formatZahl(v, 0)}
              />
              <Tooltip content={<KursTooltip currency={currency} />} />
              <Line type="monotone" dataKey="bollOben" stroke={farben.line} strokeWidth={1} strokeDasharray="4 3" dot={false} connectNulls />
              <Line type="monotone" dataKey="bollUnten" stroke={farben.line} strokeWidth={1} strokeDasharray="4 3" dot={false} connectNulls />
              <Line type="monotone" dataKey="sma200" stroke={farben.inkMuted} strokeWidth={1.3} dot={false} connectNulls />
              <Line type="monotone" dataKey="sma50" stroke={farben.brand} strokeWidth={1.3} dot={false} connectNulls />
              <Line type="monotone" dataKey="schluss" stroke={farben.ink} strokeWidth={1.8} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Abschnitt>

      {/* RSI */}
      <Abschnitt titel="Momentum · RSI (14)">
        <div className="rounded-xl border border-line bg-paper-soft p-2">
          <ResponsiveContainer width="100%" height={140}>
            <LineChart data={chart.rsiReihe} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
              <CartesianGrid stroke={farben.line} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="datum" tickFormatter={tickDatum} tick={achseStil} minTickGap={44} stroke={farben.line} />
              <YAxis domain={[0, 100]} ticks={[30, 50, 70]} tick={achseStil} width={52} stroke={farben.line} />
              <Tooltip
                formatter={(v) => [formatZahl(v, 0), 'RSI']}
                labelFormatter={(l) => new Date(l).toLocaleDateString('de-DE')}
                contentStyle={{ fontSize: 12 }}
              />
              <ReferenceLine y={70} stroke={farben.loss} strokeDasharray="4 3" />
              <ReferenceLine y={30} stroke={farben.gain} strokeDasharray="4 3" />
              <Line type="monotone" dataKey="rsi" stroke={farben.ink} strokeWidth={1.5} dot={false} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Abschnitt>

      {/* MACD */}
      <Abschnitt titel="Trend · MACD (12 / 26 / 9)">
        <div className="rounded-xl border border-line bg-paper-soft p-2">
          <ResponsiveContainer width="100%" height={160}>
            <ComposedChart data={chart.macdReihe} margin={{ top: 8, right: 12, bottom: 4, left: 4 }}>
              <CartesianGrid stroke={farben.line} strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="datum" tickFormatter={tickDatum} tick={achseStil} minTickGap={44} stroke={farben.line} />
              <YAxis tick={achseStil} width={52} stroke={farben.line} tickFormatter={(v) => formatZahl(v, 1)} />
              <Tooltip
                formatter={(v, name) => [formatZahl(v, 2), name]}
                labelFormatter={(l) => new Date(l).toLocaleDateString('de-DE')}
                contentStyle={{ fontSize: 12 }}
              />
              <ReferenceLine y={0} stroke={farben.inkMuted} />
              <Bar dataKey="histogramm" name="Histogramm" maxBarSize={6}>
                {chart.macdReihe.map((p, i) => (
                  <Cell key={i} fill={(p.histogramm ?? 0) >= 0 ? farben.gain : farben.loss} fillOpacity={0.5} />
                ))}
              </Bar>
              <Line type="monotone" dataKey="macd" name="MACD" stroke={farben.ink} strokeWidth={1.5} dot={false} connectNulls />
              <Line type="monotone" dataKey="signal" name="Signal" stroke={farben.brand} strokeWidth={1.3} dot={false} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Abschnitt>

      {/* Signale + Fazit */}
      <Abschnitt titel="Signale">
        <SignalListe signale={chart.signale} />
      </Abschnitt>
      <FazitBox>{chart.fazit}</FazitBox>
    </div>
  );
}
